import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  Calendar,
  Layers3,
  Pencil,
  Plus,
  Trash2,
  Users,
  Eye,
} from 'lucide-react'
import { programmesService } from '@/services/programmesService'
import {
  ProgrammeCohortModel,
  ProgrammeModel,
  ProgrammePaymentAccountJSON,
} from '@/models/programme.model'
import {
  useProgrammeCohorts,
  useCreateProgrammeCohort,
  useUpdateProgrammeCohort,
  useDeleteProgrammeCohort,
} from '@/hooks/useProgrammeCohorts'
import {
  ProgrammeCohortForm,
  ProgrammeCohortFormValues,
} from '@/components/app/programmes/ProgrammeCohortForm'
import { ProgrammeCohortMutationPayload } from '@/services/programmeCohortService'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDeleteDialog } from '@/components/app/common/ConfirmDeleteDialog'
import { formatCurrencyAmount } from '@/utils/currency'
import { cn } from '@/utils/cn'
import { paths } from '@/routes/path'

type BannerState = { type: 'success' | 'error'; message: string } | null

export function ProgrammeCohortsPage() {
  const { programmeId } = useParams<{ programmeId: string }>()
  const navigate = useNavigate()
  const [formOpen, setFormOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create')
  const [editingCohort, setEditingCohort] = useState<ProgrammeCohortModel | null>(null)
  const [banner, setBanner] = useState<BannerState>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [cohortToDelete, setCohortToDelete] = useState<ProgrammeCohortModel | null>(null)
  const [viewingCohort, setViewingCohort] = useState<ProgrammeCohortModel | null>(null)

  const programmeQuery = useQuery({
    queryKey: ['programmeDetails', programmeId],
    queryFn: async (): Promise<ProgrammeModel> => {
      if (!programmeId) throw new Error('Programme ID is required')
      return programmesService.getProgramme(programmeId, { requireAuth: true })
    },
    enabled: Boolean(programmeId),
    staleTime: 5 * 60 * 1000,
  })

  const cohortsQuery = useProgrammeCohorts(programmeId)
  const createMutation = useCreateProgrammeCohort(programmeId)
  const updateMutation = useUpdateProgrammeCohort(programmeId, editingCohort?.id)
  const deleteMutation = useDeleteProgrammeCohort(programmeId)

  const programme = programmeQuery.data
  const cohorts = cohortsQuery.data ?? []
  const isLoadingCohorts = cohortsQuery.isLoading
  const cohortsError = cohortsQuery.error as Error | null

  useEffect(() => {
    if (!banner) return
    const timeout = setTimeout(() => setBanner(null), 4000)
    return () => clearTimeout(timeout)
  }, [banner])

  const sortedCohorts = useMemo(() => {
    const fallbackDate = (cohort: ProgrammeCohortModel) =>
      cohort.startDate ?? cohort.createdAt ?? cohort.updatedAt ?? ''
    return cohorts
      .slice()
      .sort((a, b) => fallbackDate(a).localeCompare(fallbackDate(b)))
  }, [cohorts])

  const activeCount = sortedCohorts.filter((cohort) => cohort.isActive ?? true).length
  const totalCapacity = sortedCohorts.reduce((sum, cohort) => sum + (cohort.capacity ?? 0), 0)
  const hasCapacity = totalCapacity > 0

  const currencyOptions = useMemo(() => {
    const uniqueCurrencies = new Set<string>()
    sortedCohorts.forEach((cohort) => {
      if (cohort.currency) {
        uniqueCurrencies.add(cohort.currency)
      }
    })
    return uniqueCurrencies.size > 0 ? Array.from(uniqueCurrencies) : ['NGN', 'USD', 'GBP']
  }, [sortedCohorts])

  const formDefaults: Partial<ProgrammeCohortFormValues> | undefined = editingCohort
    ? {
      title: editingCohort.title,
      startDate: formatDateInput(editingCohort.startDate),
      endDate: formatDateInput(editingCohort.endDate),
      examDate: formatDateInput(editingCohort.examDate),
      firstYrFirstSemesterStartDate: formatDateInput(editingCohort.firstYrFirstSemesterStartDate),
      firstYrFirstSemesterEndDate: formatDateInput(editingCohort.firstYrFirstSemesterEndDate),
      firstYrSecondSemesterStartDate: formatDateInput(editingCohort.firstYrSecondSemesterStartDate),
      firstYrSecondSemesterEndDate: formatDateInput(editingCohort.firstYrSecondSemesterEndDate),
      secondYrFirstSemesterStartDate: formatDateInput(editingCohort.secondYrFirstSemesterStartDate),
      secondYrFirstSemesterEndDate: formatDateInput(editingCohort.secondYrFirstSemesterEndDate),
      secondYrSecondSemesterStartDate: formatDateInput(editingCohort.secondYrSecondSemesterStartDate),
      secondYrSecondSemesterEndDate: formatDateInput(editingCohort.secondYrSecondSemesterEndDate),
      firstYrFirstSemesterExamDate: formatDateInput(editingCohort.firstYrFirstSemesterExamDate),
      firstYrSecondSemesterExamDate: formatDateInput(editingCohort.firstYrSecondSemesterExamDate),
      secondYrFirstSemesterExamDate: formatDateInput(editingCohort.secondYrFirstSemesterExamDate),
      secondYrSecondSemesterExamDate: formatDateInput(editingCohort.secondYrSecondSemesterExamDate),
      capacity: editingCohort.capacity !== undefined ? String(editingCohort.capacity) : '',
      fee: editingCohort.fee !== undefined ? String(editingCohort.fee) : '',
      currency:
        editingCohort.currency ??
        currencyOptions[0],
      isActive: editingCohort.isActive ?? true,
      paymentAccounts: editingCohort.payment_accounts?.map((account) => ({
        bankName: account.bankName ?? '',
        bankCode: account.bankCode ?? '',
        accountName: account.accountName ?? '',
        accountNumber: account.accountNumber ?? '',
        currency: account.currency ?? 'NGN',
        instructions: account.instructions ?? '',
        isDefault: account.is_default ?? false,
        isActive: account.is_active ?? true,
      })) ?? [],
    }
    : undefined

  const openCreateModal = () => {
    setFormMode('create')
    setEditingCohort(null)
    setFormOpen(true)
  }

  const openEditModal = (cohort: ProgrammeCohortModel) => {
    setEditingCohort(cohort)
    setFormMode('edit')
    setFormOpen(true)
  }

  const closeForm = () => {
    setFormOpen(false)
    setEditingCohort(null)
  }

  const showBanner = (type: 'success' | 'error', message: string) => {
    setBanner({ type, message })
  }

  const handleFormSubmit = async (values: ProgrammeCohortFormValues) => {
    const payload = mapFormValuesToPayload(values)
    try {
      if (formMode === 'create') {
        await createMutation.mutateAsync(payload)
        showBanner('success', 'Cohort created successfully.')
      } else if (editingCohort?.id) {
        await updateMutation.mutateAsync(payload)
        showBanner('success', 'Cohort updated successfully.')
      }
      closeForm()
    } catch (error) {
      showBanner('error', (error as Error).message || 'Unable to save cohort.')
    }
  }

  const confirmDelete = (cohort: ProgrammeCohortModel) => {
    setCohortToDelete(cohort)
    setDeleteDialogOpen(true)
  }

  const handleDelete = async () => {
    if (!cohortToDelete?.id) return
    try {
      await deleteMutation.mutateAsync(cohortToDelete.id)
      showBanner('success', 'Cohort deleted successfully.')
      setDeleteDialogOpen(false)
      setCohortToDelete(null)
    } catch (error) {
      showBanner('error', (error as Error).message || 'Unable to delete cohort.')
    }
  }

  if (!programmeId) {
    return (
      <Card className="mx-auto max-w-xl">
        <CardContent className="space-y-4 p-6 text-center">
          <h1 className="text-2xl font-semibold text-secondary-900">Programme not found</h1>
          <p className="text-secondary-600">A valid programme identifier is required to manage cohorts.</p>
          <Button onClick={() => navigate(paths.app.programmes.root.absolute)}>Back to Programmes</Button>
        </CardContent>
      </Card>
    )
  }

  if (programmeQuery.isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-secondary-500">
        Loading programme…
      </div>
    )
  }

  if (programmeQuery.error) {
    return (
      <Card className="mx-auto max-w-xl">
        <CardContent className="space-y-4 p-6 text-center">
          <h1 className="text-2xl font-semibold text-secondary-900">Unable to load programme</h1>
          <p className="text-secondary-600">
            {(programmeQuery.error as Error).message || 'Please try again later.'}
          </p>
          <Button onClick={() => navigate(paths.app.programmes.root.absolute)}>Back to Programmes</Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <header className="space-y-4">
        <div className="flex flex-wrap items-center gap-3 text-sm text-secondary-500">
          <Link
            to={paths.app.programmes.detail.build(programmeId)}
            className="inline-flex items-center gap-2 text-secondary-500 hover:text-secondary-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to programme
          </Link>
          <span className="hidden sm:inline text-secondary-300">•</span>
          <span>Cohort management</span>
        </div>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">Manage cohorts</h1>
            <p className="text-secondary-600">Create, update, or retire enrolment cohorts for {programme?.title}.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => navigate(paths.app.programmes.curriculum.build(programmeId))}>
              <Layers3 className="mr-2 h-4 w-4" />
              Manage curriculum
            </Button>
            <Button onClick={openCreateModal}>
              <Plus className="mr-2 h-4 w-4" />
              Add cohort
            </Button>
          </div>
        </div>
      </header>

      {banner && (
        <div
          className={cn(
            'rounded-md border px-4 py-3 text-sm',
            banner.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-red-200 bg-red-50 text-red-700',
          )}
        >
          {banner.message}
        </div>
      )}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <SummaryCard
          icon={<Users className="h-5 w-5 text-[#7b2a40]" />}
          label="Active cohorts"
          value={activeCount}
          helper={`${sortedCohorts.length} total`}
        />
        <SummaryCard
          icon={<Calendar className="h-5 w-5 text-[#7b2a40]" />}
          label="Next start date"
          value={
            sortedCohorts.length > 0
              ? formatDateDisplay(sortedCohorts[0].startDate)
              : 'Not scheduled'
          }
          helper={sortedCohorts.length ? sortedCohorts[0].title : 'Create a cohort to publish dates'}
        />
        <SummaryCard
          icon={<Layers3 className="h-5 w-5 text-[#7b2a40]" />}
          label="Total capacity"
          value={hasCapacity ? totalCapacity.toLocaleString() : 'Not specified'}
          helper={hasCapacity ? 'Across all cohorts' : 'Add capacity to track enrolment slots'}
        />
      </section>

      <section>
        <Card>
          <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Cohort list</CardTitle>
              <p className="text-sm text-secondary-500">
                {isLoadingCohorts
                  ? 'Loading cohorts…'
                  : cohorts.length === 0
                    ? 'No cohorts have been added yet.'
                    : `Showing ${cohorts.length} cohort${cohorts.length === 1 ? '' : 's'}.`}
              </p>
            </div>
            <Button variant="outline" onClick={openCreateModal}>
              <Plus className="mr-2 h-4 w-4" />
              Add cohort
            </Button>
          </CardHeader>
          <CardContent>
            {cohortsError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {cohortsError.message || 'Unable to load cohorts.'}
              </div>
            )}
            {!isLoadingCohorts && cohorts.length === 0 && !cohortsError && (
              <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-secondary-200 p-10 text-center text-secondary-600">
                <p className="text-lg font-semibold text-secondary-800">No cohorts yet</p>
                <p className="max-w-md text-sm">
                  Create your first cohort to set programme timelines, tuition, and capacity for learners.
                </p>
                <Button onClick={openCreateModal}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create cohort
                </Button>
              </div>
            )}
            {cohorts.length > 0 && (
              <>
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-secondary-200 text-sm">
                    <thead className="bg-secondary-50 text-secondary-600">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">Cohort</th>
                        <th className="px-4 py-3 text-left font-medium">Dates</th>
                        <th className="px-4 py-3 text-left font-medium">Capacity</th>
                        <th className="px-4 py-3 text-left font-medium">Tuition</th>
                        <th className="px-4 py-3 text-left font-medium">Status</th>
                        <th className="px-4 py-3 text-right font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-100">
                      {sortedCohorts.map((cohort) => (
                        <tr key={cohort.id}>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-secondary-900">{cohort.title}</div>
                            <div className="text-xs text-secondary-500">
                              Updated {formatDateDisplay(cohort.updatedAt ?? cohort.createdAt)}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>{formatDateDisplay(cohort.startDate)}</div>
                            <div className="text-xs text-secondary-500">
                              {cohort.endDate ? `Ends ${formatDateDisplay(cohort.endDate)}` : 'Flexible'}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {cohort.capacity ? cohort.capacity.toLocaleString() : '—'}
                          </td>
                          <td className="px-4 py-3">
                            {cohort.fee
                              ? formatCurrencyAmount(cohort.fee, cohort.currency)
                              : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              className={
                                cohort.isActive ?? true
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-secondary-100 text-secondary-700'
                              }
                            >
                              {cohort.isActive ?? true ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="inline-flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-secondary-500 hover:text-secondary-900"
                                onClick={() => setViewingCohort(cohort)}
                                aria-label={`View ${cohort.title}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-secondary-500 hover:text-secondary-900"
                                onClick={() => openEditModal(cohort)}
                                aria-label={`Edit ${cohort.title}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:text-red-600"
                                onClick={() => confirmDelete(cohort)}
                                aria-label={`Delete ${cohort.title}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="space-y-4 md:hidden">
                  {sortedCohorts.map((cohort) => (
                    <div key={cohort.id} className="rounded-lg border border-secondary-200 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-secondary-900">{cohort.title}</p>
                          <p className="text-xs text-secondary-500">
                            Starts {formatDateDisplay(cohort.startDate)}
                          </p>
                        </div>
                        <Badge
                          className={
                            cohort.isActive ?? true
                              ? 'bg-green-100 text-green-800'
                              : 'bg-secondary-100 text-secondary-700'
                          }
                        >
                          {cohort.isActive ?? true ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <dt className="text-secondary-500">Ends</dt>
                          <dd className="font-medium text-secondary-900">
                            {formatDateDisplay(cohort.endDate)}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-secondary-500">Programme exam</dt>
                          <dd className="font-medium text-secondary-900">
                            {formatDateDisplay(cohort.examDate)}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-secondary-500">Capacity</dt>
                          <dd className="font-medium text-secondary-900">
                            {cohort.capacity ? cohort.capacity.toLocaleString() : 'Not set'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-secondary-500">Tuition</dt>
                          <dd className="font-medium text-secondary-900">
                            {cohort.fee
                              ? formatCurrencyAmount(cohort.fee, cohort.currency)
                              : 'Not set'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-secondary-500">Updated</dt>
                          <dd className="font-medium text-secondary-900">
                            {formatDateDisplay(cohort.updatedAt ?? cohort.createdAt)}
                          </dd>
                        </div>
                      </dl>
                      <div className="mt-4 flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setViewingCohort(cohort)}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditModal(cohort)}
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => confirmDelete(cohort)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </section>

      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={formMode === 'create' ? 'Create cohort' : 'Edit cohort'}
        size="lg"
      >
        <ProgrammeCohortForm
          mode={formMode}
          defaultValues={formDefaults}
          submitting={createMutation.isPending || updateMutation.isPending}
          onSubmit={handleFormSubmit}
          onCancel={closeForm}
          currencyOptions={currencyOptions}
          initialPaymentAccounts={formDefaults?.paymentAccounts}
        />
      </Modal>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        entityLabel="Cohort"
        entityName={cohortToDelete?.title}
        onConfirm={handleDelete}
        onClose={() => {
          setDeleteDialogOpen(false)
          setCohortToDelete(null)
        }}
        loading={deleteMutation.isPending}
      />

      <Modal
        isOpen={Boolean(viewingCohort)}
        onClose={() => setViewingCohort(null)}
        title={viewingCohort ? viewingCohort.title : 'Cohort details'}
        size="lg"
      >
        {viewingCohort && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <div className="text-sm text-secondary-500">Start date</div>
                <div className="font-medium text-secondary-900">
                  {formatDateDisplay(viewingCohort.startDate)}
                </div>
              </div>
              <div>
                <div className="text-sm text-secondary-500">End date</div>
                <div className="font-medium text-secondary-900">
                  {formatDateDisplay(viewingCohort.endDate)}
                </div>
              </div>
              <div>
                <div className="text-sm text-secondary-500">Programme exam</div>
                <div className="font-medium text-secondary-900">
                  {formatDateDisplay(viewingCohort.examDate)}
                </div>
              </div>
              <div>
                <div className="text-sm text-secondary-500">Status</div>
                <div className="font-medium text-secondary-900">
                  {viewingCohort.isActive ?? true ? 'Active' : 'Inactive'}
                </div>
              </div>
              <div>
                <div className="text-sm text-secondary-500">Capacity</div>
                <div className="font-medium text-secondary-900">
                  {viewingCohort.capacity ? viewingCohort.capacity.toLocaleString() : 'Not set'}
                </div>
              </div>
              <div>
                <div className="text-sm text-secondary-500">Tuition</div>
                <div className="font-medium text-secondary-900">
                  {viewingCohort.fee
                    ? formatCurrencyAmount(viewingCohort.fee, viewingCohort.currency)
                    : 'Not set'}
                </div>
              </div>
            </div>

            {getExamDates(viewingCohort).length > 0 && (
              <div className="space-y-2 rounded-lg border border-secondary-200 p-4 bg-secondary-50/50">
                <div className="font-medium text-secondary-900">Exam dates</div>
                <div className="space-y-1 text-sm text-secondary-700">
                  {getExamDates(viewingCohort).map((exam) => (
                    <div key={`${viewingCohort.id}-${exam.label}-detail`}>
                      <span className="font-semibold">{exam.label}:</span> {exam.date}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {getSemesterDateRanges(viewingCohort).length > 0 && (
              <div className="space-y-2 rounded-lg border border-secondary-200 p-4 bg-secondary-50/50">
                <div className="font-medium text-secondary-900">Semester timelines</div>
                <div className="space-y-1 text-sm text-secondary-700">
                  {getSemesterDateRanges(viewingCohort).map((range) => (
                    <div key={`${viewingCohort.id}-${range.label}-timeline`}>
                      <span className="font-semibold">{range.label}:</span> {range.range}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {viewingCohort.payment_accounts.length > 0 && (
              <div className="space-y-3">
                <div className="font-medium text-secondary-900 border-b border-secondary-100 pb-2">Payment accounts</div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {viewingCohort.payment_accounts.map((account, index) => (
                    <div
                      key={`${account.accountNumber ?? index}-${account.bankName ?? 'bank'}-view`}
                      className="rounded-lg border border-secondary-200 p-3 bg-white"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-semibold text-secondary-900 uppercase">
                          {account.bankName ?? 'Bank'}
                        </div>
                        <div className="flex gap-1">
                          {account.is_default && <Badge className="text-[10px] h-4">Default</Badge>}
                          {!account.is_active && <Badge variant="outline" className="text-[10px] h-4">Inactive</Badge>}
                        </div>
                      </div>
                      <dl className="space-y-1 text-xs text-secondary-600">
                        <div className="flex justify-between">
                          <dt>Account Name</dt>
                          <dd className="font-medium text-secondary-900">{account.accountName ?? 'N/A'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt>Account Number</dt>
                          <dd className="font-medium text-secondary-900">{account.accountNumber ?? 'N/A'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt>Currency</dt>
                          <dd className="font-medium text-secondary-900">{account.currency ?? 'N/A'}</dd>
                        </div>
                        {account.instructions && (
                          <div className="pt-1 mt-1 border-t border-secondary-50 italic">
                            {account.instructions}
                          </div>
                        )}
                      </dl>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}

interface SummaryCardProps {
  icon: React.ReactNode
  label: string
  value: React.ReactNode
  helper?: string
}

const SummaryCard: React.FC<SummaryCardProps> = ({ icon, label, value, helper }) => {
  return (
    <Card className="border-secondary-200">
      <CardContent className="flex items-center gap-4 p-5">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary-50">
          {icon}
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-secondary-500">{label}</div>
          <div className="text-2xl font-semibold text-secondary-900">{value}</div>
          {helper && <p className="text-xs text-secondary-500">{helper}</p>}
        </div>
      </CardContent>
    </Card>
  )
}

function formatDateDisplay(value?: string | null): string {
  if (!value) {
    return 'Not set'
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatSemesterRange(start?: string | null, end?: string | null): string | undefined {
  if (start && end) {
    return `${formatDateDisplay(start)} - ${formatDateDisplay(end)}`
  }
  if (start) return `From ${formatDateDisplay(start)}`
  if (end) return `Until ${formatDateDisplay(end)}`
  return undefined
}

function getSemesterDateRanges(cohort: ProgrammeCohortModel): { label: string; range: string }[] {
  const ranges: { label: string; range: string | undefined }[] = [
    {
      label: 'Year 1 • Semester 1',
      range: formatSemesterRange(cohort.firstYrFirstSemesterStartDate, cohort.firstYrFirstSemesterEndDate),
    },
    {
      label: 'Year 1 • Semester 2',
      range: formatSemesterRange(cohort.firstYrSecondSemesterStartDate, cohort.firstYrSecondSemesterEndDate),
    },
    {
      label: 'Year 2 • Semester 1',
      range: formatSemesterRange(cohort.secondYrFirstSemesterStartDate, cohort.secondYrFirstSemesterEndDate),
    },
    {
      label: 'Year 2 • Semester 2',
      range: formatSemesterRange(cohort.secondYrSecondSemesterStartDate, cohort.secondYrSecondSemesterEndDate),
    },
  ]

  return ranges.filter((item): item is { label: string; range: string } => Boolean(item.range))
}

function formatExamDate(value?: string | null): string | undefined {
  if (!value) return undefined
  return formatDateDisplay(value)
}

function getExamDates(cohort: ProgrammeCohortModel): { label: string; date: string }[] {
  const dates: { label: string; date: string | undefined }[] = [
    { label: 'Programme exam', date: formatExamDate(cohort.examDate) },
    { label: 'Year 1 • Semester 1', date: formatExamDate(cohort.firstYrFirstSemesterExamDate) },
    { label: 'Year 1 • Semester 2', date: formatExamDate(cohort.firstYrSecondSemesterExamDate) },
    { label: 'Year 2 • Semester 1', date: formatExamDate(cohort.secondYrFirstSemesterExamDate) },
    { label: 'Year 2 • Semester 2', date: formatExamDate(cohort.secondYrSecondSemesterExamDate) },
  ]

  return dates.filter((item): item is { label: string; date: string } => Boolean(item.date))
}

function formatDateInput(value?: string | null): string | undefined {
  if (!value) return undefined
  return value.split('T')[0]
}

function mapFormValuesToPayload(values: ProgrammeCohortFormValues): ProgrammeCohortMutationPayload {
  const normalizeNumber = (raw?: string | null): number | null => {
    if (!raw) return null
    const trimmed = raw.trim()
    if (!trimmed) return null
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : null
  }

  return {
    title: values.title.trim(),
    startDate: values.startDate || null,
    endDate: values.endDate || null,
    examDate: values.examDate || null,
    firstYrFirstSemesterStartDate: values.firstYrFirstSemesterStartDate || null,
    firstYrFirstSemesterEndDate: values.firstYrFirstSemesterEndDate || null,
    firstYrSecondSemesterStartDate: values.firstYrSecondSemesterStartDate || null,
    firstYrSecondSemesterEndDate: values.firstYrSecondSemesterEndDate || null,
    secondYrFirstSemesterStartDate: values.secondYrFirstSemesterStartDate || null,
    secondYrFirstSemesterEndDate: values.secondYrFirstSemesterEndDate || null,
    secondYrSecondSemesterStartDate: values.secondYrSecondSemesterStartDate || null,
    secondYrSecondSemesterEndDate: values.secondYrSecondSemesterEndDate || null,
    firstYrFirstSemesterExamDate: values.firstYrFirstSemesterExamDate || null,
    firstYrSecondSemesterExamDate: values.firstYrSecondSemesterExamDate || null,
    secondYrFirstSemesterExamDate: values.secondYrFirstSemesterExamDate || null,
    secondYrSecondSemesterExamDate: values.secondYrSecondSemesterExamDate || null,
    capacity: normalizeNumber(values.capacity),
    fee: normalizeNumber(values.fee),
    currency: values.currency || null,
    isActive: values.isActive,
    payment_accounts: values.paymentAccounts?.map((account) => ({
      bank_name: account.bankName,
      bank_code: account.bankCode || undefined,
      account_name: account.accountName,
      account_number: account.accountNumber,
      currency: account.currency,
      instructions: account.instructions || undefined,
      is_default: account.isDefault,
      is_active: account.isActive,
    })) as ProgrammePaymentAccountJSON[],
  }
}

export default ProgrammeCohortsPage
